import React from 'react';
import { Dropdown, NewModal, RadioInput } from 'lib/components';
import { Button } from 'react-covideo-common';
import { Gap, Stack } from 'lib/components/styles/layout';
import {
  ParagraphNormal,
  ParagraphSmall,
} from 'lib/components/styles/typography';
import { useRecordSettings } from 'lib/context/record/provider';
import { VIDEO_QUALITY } from 'lib/context/record/types';
import styled, { useTheme } from 'styled-components/macro';
import { theme } from 'lib/style';
import { useCDK } from 'lib/hooks/useCdk';
import { useTranslation } from 'react-i18next';
import { getChangedFields } from 'lib/hooks/GTM/utils';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import { RECORDING_SETTINGS_TRACKED_FIELDS } from 'lib/hooks/GTM/trackedFields';

const RadioLabel = styled.label`
  display: flex;
  gap: 4px;
  color: ${theme.palette.black_1_100};
  cursor: pointer;
`;

interface IProps {
  handleModalClose: () => void;
}

type Option = {
  label: string;
  value: string;
};

export const ModalRecordingVideosSettings = ({ handleModalClose }: IProps) => {
  const { colors } = useTheme();
  const { secondaryToText } = useCDK();
  const { t } = useTranslation();
  const { trackRecordingSettingsInteractionEvent } = useGTMAnalytics();
  const {
    recorderSettings,
    setUserSettings,
    userSettings,
    onSubmitModalHandler,
    initialUserSettings,
  } = useRecordSettings();

  const trackRecordingSettingsChanges = async (userSettings: any) => {
    const { changed_fields, ...rest } = getChangedFields({
      initialValues: initialUserSettings,
      currentValues: userSettings,
      trackedFields: RECORDING_SETTINGS_TRACKED_FIELDS,
      onlyChangedList: false,
    });

    await trackRecordingSettingsInteractionEvent?.({
      fieldChanges: rest,
      updatedFields: changed_fields,
    });
  };

  const onSubmit = async () => {
    onSubmitModalHandler();
    await trackRecordingSettingsChanges(userSettings);
    handleModalClose();
  };

  const onCancelOrCloseModalSettings = () => {
    setUserSettings(initialUserSettings);
    handleModalClose();
  };

  const onChangeVideoSettingsHandler = (value: Option) =>
    setUserSettings({
      ...userSettings,
      videoSource: value,
    });

  const onChangeAudioSettingsHandler = (value: Option) =>
    setUserSettings({
      ...userSettings,
      audioSource: value,
    });

  const onVideoQualityHandler = (videoQuality: VIDEO_QUALITY) =>
    setUserSettings({
      ...userSettings,
      videoQuality,
    });

  return (
    <NewModal
      headerText={t('Recording settings')}
      closeModal={onCancelOrCloseModalSettings}
      style={{
        content: {
          maxWidth: '592px',
        },
      }}
    >
      <Gap>
        {!!recorderSettings.videoSource.length && (
          <Stack width='100%' alignItems='start' gap='4px'>
            <ParagraphSmall color={colors.neutral[60]}>
              {t('Video source')}
            </ParagraphSmall>
            <Dropdown
              creatable={false}
              value={userSettings.videoSource}
              placeholder={t('No settings')}
              options={recorderSettings.videoSource}
              onChange={onChangeVideoSettingsHandler}
              height={40}
              extendStyles={{ container: { width: '100%' } }}
              isSearchable={false}
            />
          </Stack>
        )}
        {!!recorderSettings.audioSource.length && (
          <Stack width='100%' alignItems='start' gap='4px'>
            <ParagraphSmall color={colors.neutral[60]}>
              {t('Audio source')}
            </ParagraphSmall>
            <Dropdown
              creatable={false}
              placeholder={t('No settings')}
              value={userSettings.audioSource}
              options={recorderSettings.audioSource}
              onChange={onChangeAudioSettingsHandler}
              extendStyles={{ container: { width: '100%' } }}
              height={40}
              isSearchable={false}
            />
          </Stack>
        )}
        <>
          <ParagraphSmall color={colors.neutral[60]}>
            {t('Video quality')}
          </ParagraphSmall>
          <Stack alignItems='flex-start' width='100%' gap='8px' m='0 0 16px 0'>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.LOW}
                  name='quality'
                  value={VIDEO_QUALITY.LOW}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.LOW)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.LOW)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('Low')}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall>{t('Faster Upload')}</ParagraphSmall>
            </Gap>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.STANDARD}
                  name='quality'
                  value={VIDEO_QUALITY.STANDARD}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.STANDARD)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.STANDARD)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('Standard')}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall> {t('Fast Upload')}</ParagraphSmall>
            </Gap>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.HIGH}
                  name='quality'
                  value={VIDEO_QUALITY.HIGH}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.HIGH)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.HIGH)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('High')}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall>{t('Slow Upload')}</ParagraphSmall>
            </Gap>
          </Stack>
        </>
      </Gap>
      <Gap width='100%' justifyContent='flex-end'>
        <Button
          variant={secondaryToText}
          text={t('Cancel')}
          onClick={onCancelOrCloseModalSettings}
        />
        <Button text={t('Save')} onClick={onSubmit} />
      </Gap>
    </NewModal>
  );
};
